import { useState, useMemo, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { addMonths, differenceInDays, parseISO } from 'date-fns';
import V2ThemeProvider from 'components/v2/ThemeProvider';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BaseModal from 'components/v2/Modals/BaseModal';
import InputRhf from 'components/v2/FormElements/InputRhf';
import ClientAutocomplete from 'components/v2/ClientAutocomplete';
import { useUser } from 'common/context/UserContext';
import { useResolveClient } from 'hooks/useResolveClient';
import TravelerSection from './TravelerSection';
import SelectableChip from './SelectableChip';
import { SAVED_ITINERARY_TAG_OPTIONS, MONTH_NAMES } from 'itrvl-types';
import { useEdgeEnquiryZap } from 'mutations/zaps';
import EdgeCalendarPicker from './EdgeCalendarPicker';
import EdgeEnquiryConfirmModal from './EdgeEnquiryConfirmModal';
import {
  SectionBox,
  SectionTitle,
  ChipWrapper,
  SubsectionLabel,
  DropdownLabel,
  StyledRadioLabel,
  FooterButton,
  ClearButton,
  CancelButton,
  ClientDetailsRow,
  FooterRow,
} from './EdgeEnquiryModal.styles';

const schema = yup.object().shape({
  client: yup.mixed().required('Client name is required'),
  destinations: yup.array().min(1, 'Select at least one destination'),
  dateMode: yup.string(),
  selectedMonths: yup.array().when('dateMode', {
    is: 'flexible',
    then: s => s.min(1, 'Select at least one month'),
    otherwise: s => s,
  }),
  duration: yup.string().when('dateMode', {
    is: 'flexible',
    then: s => s.required('Trip duration is required'),
    otherwise: s => s,
  }),
  startDate: yup
    .string()
    .nullable()
    .when('dateMode', {
      is: 'specific',
      then: s => s.required('Select a start date'),
      otherwise: s => s,
    }),
  endDate: yup
    .string()
    .nullable()
    .when('dateMode', {
      is: 'specific',
      then: s => s.required('Select an end date'),
      otherwise: s => s,
    }),
});

const DESTINATIONS = [
  { label: 'Botswana', code: 'BW' },
  { label: 'Kenya', code: 'KE' },
  { label: 'Mauritius', code: 'MU' },
  { label: 'Mozambique', code: 'MZ' },
  { label: 'Seychelles', code: 'SC' },
  { label: 'South Africa', code: 'ZA' },
  { label: 'Tanzania', code: 'TZ' },
  { label: 'Uganda', code: 'UG' },
  { label: 'Zambia', code: 'ZM' },
  { label: 'Rwanda', code: 'RW' },
  { label: 'Namibia', code: 'NA' },
  { label: 'Zimbabwe', code: 'ZW' },
];

const codeToDestLabel = DESTINATIONS.reduce((map, d) => {
  map[d.code] = d.label;
  return map;
}, {});

const BUDGET_OPTIONS = ['Average ($5,000 - $10,000)', 'High ($10,000 - $15,000)', 'Luxury (Over $15,000)'];

const DURATION_OPTIONS = ['Short (4-6 Nights)', 'Average (7-9 Nights)', 'Long (10-13 Nights)', 'Extended (14+ Nights)'];

const inputSx = {
  backgroundColor: 'common.white',
};

const FORM_ID = 'edge-enquiry-form';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear + 1, currentYear + 2];

const defaultDate = addMonths(new Date(), 5);
const DEFAULT_MONTH = defaultDate.getMonth();
const DEFAULT_YEAR = defaultDate.getFullYear();

export default NiceModal.create(function EdgeEnquiryModal({
  initialAdults = 2,
  initialChildren = 0,
  initialChildrenAges = [],
  initialMonths = [DEFAULT_MONTH],
  initialYear = DEFAULT_YEAR,
  initialStartDate = null,
  initialEndDate = null,
  initialNotes = '',
  renderData = null,
}) {
  const modal = useModal();
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const resolveClient = useResolveClient();
  const { mutate: sendEnquiry } = useEdgeEnquiryZap();

  const agentEmail = user?.user?.email || '';

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      client: user?.client || null,
      destinations: (renderData?.savedDetails?.countries || []).map(c => codeToDestLabel[c]).filter(Boolean),
      dateMode: 'flexible',
      selectedMonths: initialMonths,
      selectedYear: initialYear,
      startDate: initialStartDate,
      endDate: initialEndDate,
      duration: '',
      adults: initialAdults,
      children: initialChildren,
      childrenAges: initialChildrenAges,
      budget: '',
      tripTypes: renderData?.savedDetails?.tags || [],
      notes: initialNotes,
    },
  });

  const dateMode = watch('dateMode');
  const adults = watch('adults');
  const children = watch('children');
  const childrenAges = watch('childrenAges');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const computedStayNights = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return differenceInDays(parseISO(endDate), parseISO(startDate));
  }, [startDate, endDate]);

  const handleDateSelect = useCallback(
    dateString => {
      if (!startDate || (startDate && endDate)) {
        setValue('startDate', dateString);
        setValue('endDate', null);
      } else if (dateString === startDate) {
        setValue('startDate', null);
        setValue('endDate', null);
      } else if (dateString < startDate) {
        setValue('endDate', startDate);
        setValue('startDate', dateString);
      } else {
        setValue('endDate', dateString);
      }
    },
    [startDate, endDate, setValue],
  );

  const handleRangeSelect = useCallback(
    (start, end) => {
      setValue('startDate', start);
      setValue('endDate', end);
    },
    [setValue],
  );

  const [calendarMonth, setCalendarMonth] = useState(initialMonths[0] ?? new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(initialYear);
  const calendarInitialMonth = useMemo(() => new Date(calendarYear, calendarMonth), [calendarYear, calendarMonth]);

  const handleAdultsChange = useCallback(v => setValue('adults', Math.max(1, v)), [setValue]);

  const handleChildrenChange = useCallback(
    newCount => {
      const count = Math.max(0, newCount);
      setValue('children', count);
      const current = getValues('childrenAges');
      if (count > current.length) {
        setValue('childrenAges', [...current, ...Array.from({ length: count - current.length }, () => 12)]);
      } else {
        setValue('childrenAges', current.slice(0, count));
      }
    },
    [getValues, setValue],
  );

  const handleChildAgeChange = useCallback(
    (idx, value) => {
      const numeric = value.replace(/\D/g, '');
      const current = [...getValues('childrenAges')];
      current[idx] = numeric === '' ? '' : Math.min(Number(numeric), 17);
      setValue('childrenAges', current);
    },
    [getValues, setValue],
  );

  const handleChildAgeBlur = useCallback(
    idx => {
      const current = [...getValues('childrenAges')];
      const parsed = Number(current[idx]);
      current[idx] = parsed >= 1 && parsed <= 17 ? parsed : 12;
      setValue('childrenAges', current);
    },
    [getValues, setValue],
  );

  const handleClientChange = useCallback(
    val => {
      setValue('client', val);
      if (val?.id) {
        user.updateClient(val);
      }
    },
    [setValue, user],
  );

  const handleClearForm = useCallback(() => {
    reset({
      destinations: [],
      dateMode: 'flexible',
      selectedMonths: [DEFAULT_MONTH],
      selectedYear: DEFAULT_YEAR,
      startDate: null,
      endDate: null,
      duration: '',
      adults: 2,
      children: 0,
      childrenAges: [],
      budget: '',
      tripTypes: [],
      notes: '',
    });
  }, [reset]);

  // @todo pass field.ref/inputRef through Controllers so RHF shouldFocusError works natively
  const onError = useCallback(() => {
    requestAnimationFrame(() => {
      const form = document.getElementById(FORM_ID);
      const errorEl = form?.querySelector('.Mui-error');
      if (errorEl) errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  const onSubmit = useCallback(
    async formData => {
      let resolved;
      try {
        resolved = await resolveClient(formData.client);
      } catch {
        enqueueSnackbar('Unable to create client. Please try again.', { variant: 'error' });
        return;
      }
      if (!resolved) return;
      const payload =
        formData.dateMode === 'flexible'
          ? { ...formData, startDate: '', endDate: '' }
          : { ...formData, selectedMonths: [], selectedYear: '', duration: '' };
      sendEnquiry({ formData: payload, renderData });
      modal.hide();
      NiceModal.show(EdgeEnquiryConfirmModal);
    },
    [modal, sendEnquiry, resolveClient, renderData, enqueueSnackbar],
  );

  const footerControls = (
    <FooterRow direction="row" alignItems="center" justifyContent="space-between">
      <ClearButton variant="outlined" onClick={handleClearForm}>
        Clear Form
      </ClearButton>
      <Stack direction="row" gap={1}>
        <CancelButton onClick={modal.hide}>Cancel</CancelButton>
        <FooterButton variant="contained" type="submit" form={FORM_ID}>
          Submit
        </FooterButton>
      </Stack>
    </FooterRow>
  );

  return (
    <V2ThemeProvider>
      <BaseModal
        title="Request Custom Quote"
        subtext="Share your client's travel preferences below. Wilderness will email you a custom quote within one business day."
        disableHide
        maxWidth="md"
        fullWidth
        controls={footerControls}
        paperProps={{
          sx: { maxHeight: '90vh' },
          'data-test-id': 'edge-enquiry-modal',
        }}
      >
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit, onError)}>
          <Stack gap={3}>
            {/* Client Details */}
            <SectionBox>
              <SectionTitle variant="body1">Contact Information</SectionTitle>
              <ClientDetailsRow gap={3}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Controller
                    name="client"
                    control={control}
                    render={({ field }) => <ClientAutocomplete value={field.value} onChange={handleClientChange} />}
                  />
                  {errors.client && <FormHelperText error>{errors.client.message}</FormHelperText>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField value={agentEmail} label="Your Email" size="small" fullWidth disabled sx={inputSx} />
                </Box>
              </ClientDetailsRow>
            </SectionBox>

            {/* Destination Details */}
            <SectionBox>
              <SectionTitle variant="body1">Destination Details</SectionTitle>
              <Box>
                <SubsectionLabel variant="subtitle2">Preferred Destinations</SubsectionLabel>
                <Controller
                  name="destinations"
                  control={control}
                  render={({ field }) => (
                    <ChipWrapper>
                      {DESTINATIONS.map(({ label }) => (
                        <SelectableChip
                          key={label}
                          label={label}
                          selected={field.value.includes(label)}
                          onClick={() => {
                            const next = field.value.includes(label) ? field.value.filter(d => d !== label) : [...field.value, label];
                            field.onChange(next);
                          }}
                        />
                      ))}
                    </ChipWrapper>
                  )}
                />
                {errors.destinations && <FormHelperText error>{errors.destinations.message}</FormHelperText>}
              </Box>
            </SectionBox>

            {/* Preferred Dates */}
            <SectionBox>
              <Stack gap={1.25}>
                <SectionTitle variant="body1">Preferred Dates</SectionTitle>
                <Typography variant="caption" sx={{ color: 'text.primary' }}>
                  Choose dates or a flexible window
                </Typography>
              </Stack>
              <Controller
                name="dateMode"
                control={control}
                render={({ field }) => (
                  <RadioGroup row value={field.value} onChange={field.onChange} sx={{ gap: 3 }}>
                    <StyledRadioLabel value="flexible" control={<Radio />} label="Flexible Dates" />
                    <StyledRadioLabel value="specific" control={<Radio />} label="Specific Dates" />
                  </RadioGroup>
                )}
              />

              {dateMode === 'flexible' ? (
                <Stack gap={3}>
                  <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ flexWrap: 'wrap' }}>
                    <Box>
                      <DropdownLabel variant="subtitle2">Month</DropdownLabel>
                      <Controller
                        name="selectedMonths"
                        control={control}
                        render={({ field }) => (
                          <Select
                            multiple
                            value={field.value}
                            onChange={field.onChange}
                            size="small"
                            error={!!errors.selectedMonths}
                            sx={{ ...inputSx, minWidth: 200 }}
                            inputProps={{ 'aria-label': 'Month' }}
                            displayEmpty
                            renderValue={sel =>
                              sel.length === 0
                                ? 'Month'
                                : [...sel]
                                    .sort((a, b) => a - b)
                                    .map(i => MONTH_NAMES[i])
                                    .join(', ')
                            }
                          >
                            {MONTH_NAMES.map((name, idx) => (
                              <MenuItem key={name} value={idx}>
                                <Checkbox checked={field.value.includes(idx)} size="small" />
                                <ListItemText primary={name} />
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.selectedMonths && <FormHelperText error>{errors.selectedMonths.message}</FormHelperText>}
                    </Box>
                    <Box>
                      <DropdownLabel variant="subtitle2">Year</DropdownLabel>
                      <Controller
                        name="selectedYear"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={field.onChange}
                            size="small"
                            sx={{ ...inputSx, width: 200 }}
                            inputProps={{ 'aria-label': 'Year' }}
                          >
                            {YEAR_OPTIONS.map(year => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </Box>
                  </Stack>
                  <Box>
                    <DropdownLabel variant="subtitle2">Trip Duration</DropdownLabel>
                    <Controller
                      name="duration"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          size="small"
                          displayEmpty
                          error={!!error}
                          sx={{ ...inputSx, width: { xs: '100%', sm: 360 } }}
                          inputProps={{ 'aria-label': 'Trip Duration' }}
                          renderValue={v => v || 'Trip Duration'}
                        >
                          {DURATION_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.duration && <FormHelperText error>{errors.duration.message}</FormHelperText>}
                  </Box>
                </Stack>
              ) : (
                <Stack gap={3}>
                  {(errors.startDate || errors.endDate) && (
                    <FormHelperText error>{errors.startDate?.message || errors.endDate?.message}</FormHelperText>
                  )}
                  <EdgeCalendarPicker
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    year={calendarYear}
                    onYearChange={setCalendarYear}
                    sx={{ maxWidth: 440 }}
                    selectedDate={startDate}
                    onSelectDate={handleDateSelect}
                    onRangeSelect={handleRangeSelect}
                    stayNights={computedStayNights}
                    adults={adults}
                    childrenAges={childrenAges}
                    initialMonth={calendarInitialMonth}
                    skipAvailability
                  />
                </Stack>
              )}
            </SectionBox>

            {/* Traveler Details */}
            <SectionBox>
              <SectionTitle variant="body1">Traveler Details</SectionTitle>
              <TravelerSection
                adults={adults}
                children={children}
                childrenAges={childrenAges}
                onAdultsChange={handleAdultsChange}
                onChildrenChange={handleChildrenChange}
                onChildAgeChange={handleChildAgeChange}
                onChildAgeBlur={handleChildAgeBlur}
                inputSx={inputSx}
              />
              <Box>
                <DropdownLabel variant="subtitle2">Budget Per Person</DropdownLabel>
                <Controller
                  name="budget"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                      displayEmpty
                      fullWidth
                      sx={inputSx}
                      inputProps={{ 'aria-label': 'Budget per person' }}
                      renderValue={v => v || 'Budget per person'}
                    >
                      {BUDGET_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
              <Box>
                <SubsectionLabel variant="subtitle2">Trip Type</SubsectionLabel>
                <Controller
                  name="tripTypes"
                  control={control}
                  render={({ field }) => (
                    <ChipWrapper>
                      {SAVED_ITINERARY_TAG_OPTIONS.map(({ label, value }) => (
                        <SelectableChip
                          key={value}
                          label={label}
                          selected={field.value.includes(value)}
                          onClick={() => {
                            const next = field.value.includes(value) ? field.value.filter(v => v !== value) : [...field.value, value];
                            field.onChange(next);
                          }}
                        />
                      ))}
                    </ChipWrapper>
                  )}
                />
              </Box>
            </SectionBox>

            {/* Other Details */}
            <SectionBox>
              <SectionTitle variant="body1">Other Details</SectionTitle>
              <InputRhf
                name="notes"
                control={control}
                label="Additional Notes"
                placeholder="Add extra details to share with Wilderness"
                size="small"
                multiline
                minRows={4}
                sx={inputSx}
              />
            </SectionBox>
          </Stack>
        </form>
      </BaseModal>
    </V2ThemeProvider>
  );
});
